import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { checkInFollowupAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from './ToastNotification';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const POLL_MS = 60_000;

/**
 * Polls check-in follow-up prompts (vibe + solo note) after server marks jobs `sent`.
 * Web push can be layered later; this gives an in-app path when the app is open.
 */
const CheckInFollowupInbox = () => {
  const { isAuthenticated, user } = useAuth();
  const { showError, showSuccess } = useToast();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [soloDraft, setSoloDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dialogActiveRef = useRef(false);

  const current = queue[0] || null;

  useEffect(() => {
    dialogActiveRef.current = open && queue.length > 0;
  }, [open, queue.length]);

  const pull = useCallback(async () => {
    if (!isAuthenticated || !user?.id || location.pathname.startsWith('/admin')) return;
    try {
      const data = await checkInFollowupAPI.getInbox({
        token: user?.access_token,
        userId: user.id,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      if (dialogActiveRef.current) return;
      setQueue(items);
      if (items.length > 0) setOpen(true);
    } catch {
      /* tables may not exist yet; stay quiet */
    }
  }, [isAuthenticated, user?.id, user?.access_token, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || location.pathname.startsWith('/admin')) return undefined;
    pull();
    const t = setInterval(pull, POLL_MS);
    return () => clearInterval(t);
  }, [isAuthenticated, user?.id, location.pathname, pull]);

  useEffect(() => {
    if (current?.prompt_type === 'solo_note') {
      setSoloDraft('');
    }
  }, [current?.job_id, current?.prompt_type]);

  const advanceQueue = () => {
    setQueue((q) => {
      const next = q.slice(1);
      if (next.length === 0) setOpen(false);
      return next;
    });
    setSoloDraft('');
  };

  const onVibe = async (vibeStatus) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      await checkInFollowupAPI.submitVibe({
        checkInId: current.check_in_id,
        vibeStatus,
        token: user?.access_token,
        userId: user?.id,
      });
      showSuccess('Thanks — that helps other solos.');
      advanceQueue();
    } catch (e) {
      showError(e?.message || 'Could not save vibe');
    } finally {
      setSubmitting(false);
    }
  };

  const onSoloSubmit = async () => {
    if (!current || submitting) return;
    const text = soloDraft.trim();
    if (!text) {
      showError('Add a short note (one or two lines).');
      return;
    }
    setSubmitting(true);
    try {
      await checkInFollowupAPI.submitSoloNote({
        checkInId: current.check_in_id,
        soloNote: text,
        token: user?.access_token,
        userId: user?.id,
      });
      showSuccess('Thanks for the solo tip.');
      advanceQueue();
    } catch (e) {
      showError(e?.message || 'Could not save note');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated || location.pathname.startsWith('/admin')) return null;

  return (
    <Dialog
      open={open && Boolean(current)}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQueue([]);
      }}
    >
      <DialogContent className="border border-white/10 bg-[#121214] text-white sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-white">
            {current?.title || 'Quick check-in'}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-white/55">
            {current?.venue_name ? `At ${current.venue_name}.` : null}
          </DialogDescription>
        </DialogHeader>

        {current?.prompt_type === 'vibe' && (
          <div className="grid grid-cols-2 gap-2">
            {(current.vibe_actions || []).map((a) => (
              <button
                key={a.id}
                type="button"
                disabled={submitting}
                onClick={() => onVibe(a.id)}
                className="rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold text-white ring-1 ring-white/15 transition hover:bg-white/15 disabled:opacity-50"
              >
                {a.label}
              </button>
            ))}
          </div>
        )}

        {current?.prompt_type === 'solo_note' && (
          <div className="space-y-2">
            <textarea
              value={soloDraft}
              onChange={(e) => setSoloDraft(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder="e.g. Solo seats at the bar, avoid the door list after 10…"
              className="w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
            <div className="text-[11px] text-white/40">{soloDraft.length}/400</div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:justify-end">
          {current?.prompt_type === 'solo_note' && (
            <button
              type="button"
              disabled={submitting}
              onClick={onSoloSubmit}
              className="w-full rounded-xl bg-[#ff2774] px-4 py-3 text-sm font-semibold text-white hover:bg-[#e91e63] disabled:opacity-50"
            >
              Send tip
            </button>
          )}
          <button
            type="button"
            onClick={() => advanceQueue()}
            className="w-full text-center text-[13px] font-medium text-white/45 hover:text-white/70"
          >
            Not now
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckInFollowupInbox;
